import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { environment } from '@environments/environment';
import { IUser, UserService } from '@shared/services/user.service';
import { catchError, retry, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { IMesUser } from './mesuser.service';

@Injectable({
	providedIn: 'root',
})
export class DepartmentService {
	constructor(
		private httpClient: HttpClient,
		private userService: UserService,
	) {}

	getDepartments(): Observable<IDepartments> {
		const apiUrl = `${environment.awsEnvUri}/mes/departments`;
		return this.httpClient
			.get<IDepartments>(apiUrl, {
				headers: this.userService.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createDepartment(department: IDepartmentDetail | IBaseDepartment): Observable<IRequestDepartment> {
		const body: IRequestDepartment = {
			id: department.id,
			alias: department.alias,
			name: department.name,
			isActive: department.isActive,
			shiftApprovers: [],
		};

		if ('shiftApprover' in department) {
			body.shiftApprovers = [department.shiftApprover.id, department.backupShiftApprover?.id].filter(Boolean);
		} else {
			body.shiftApprovers = department.shiftApprovers;
		}

		const apiUrl = `${environment.awsEnvUri}/mes/departments`;
		return this.httpClient
			.post<IRequestDepartment>(apiUrl, body, {
				headers: this.userService.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	updateDepartment(department: IDepartmentDetail | IBaseDepartment): Observable<IResponseDepartment> {
		const body: IRequestDepartment = {
			id: department.id,
			alias: department.alias,
			name: department.name,
			isActive: department.isActive,
			shiftApprovers: [],
		};

		if ('shiftApprover' in department) {
			body.shiftApprovers = [department.shiftApprover.id, department.backupShiftApprover?.id].filter(Boolean);
		} else {
			body.shiftApprovers = department.shiftApprovers;
		}

		const apiUrl = `${environment.awsEnvUri}/mes/departments/${department.id}`;
		return this.httpClient
			.put<IRequestDepartment>(apiUrl, body, {
				headers: this.userService.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getLastSegment(urlOrObject: string | any, keyPath?: string[]) {
		let url: string;

		if (typeof urlOrObject === 'string') {
			url = urlOrObject;
		} else if (Array.isArray(keyPath)) {
			url = keyPath.reduce((acc, key) => acc[key], urlOrObject);
		} else {
			throw new TypeError('Invalid arguments');
		}

		const parts = url.split('/');
		return parts[parts.length - 1];
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}
}

export interface IBaseDepartment {
	id: string;
	alias: string;
	name: string;
	isActive: boolean;
	shiftApprovers: string[];
}

export interface IDepartmentDetail extends IBaseDepartment {
	shiftApprover: IUser;
	backupShiftApprover: IUser;
	employees: MatTableDataSource<IMesUser>;
}

export interface IDepartments {
	statusCode: number;
	result: IBaseDepartment[];
}

export interface IRequestDepartment extends IBaseDepartment {
	// No additional properties
}

export interface IResponseDepartment extends IBaseDepartment {
	// No addiotional properties
}
